/**
 * Private-route chunk prefetching (#146).
 *
 * The three PRIVATE routes (`/transfers`, `/contacts`, `/account`) are code-split into
 * their own lazy chunks (see `routes.tsx`) so the heavy P2P/account code never lands in the eager
 * bundle every visitor downloads. The cost is that the FIRST open of one of these features pays a
 * network round-trip to fetch its chunk. This module warms those chunks ahead of the click — on
 * pointer/focus intent over a nav link, and once via an idle warm-up after login — so the open is
 * effectively instant.
 *
 * The loaders use the SAME module specifiers as the `React.lazy` calls in `routes.tsx`, so Vite/Rollup
 * resolve them to the SAME chunk (dedup by resolved module id): a prefetch here populates the exact
 * module the route later renders, and nothing new is emitted into the eager bundle.
 *
 * All of it is guarded by the browser's data-saver preference and is SSG-safe — `navigator` is only
 * read inside function bodies (never at module scope), so importing this module during pre-render is
 * inert.
 */

/**
 * The three private-route chunk loaders, keyed by their route path. File requests have no chunk of their
 * own since kitecorp/transferify-ui#3140: they are the Requested tab of Transfers, so they ship in its chunk.
 */
export const PRIVATE_ROUTE_LOADERS: Record<string, () => Promise<unknown>> = {
	"/transfers": () => import("../pages/TransfersRoute"),
	"/contacts": () => import("../pages/ContactsRoute"),
	"/account": () => import("../pages/AccountRoute"),
};

/** The subset of the Network Information API this module reads (the rest is irrelevant here). */
interface SaveDataConnection {
	saveData?: boolean;
}

/**
 * Decide whether prefetching is allowed for the given connection. Prefetch on a normal connection or
 * when the Network Information API is unavailable (`null`/`undefined`); skip ONLY when the user has
 * explicitly asked the browser to save data (`saveData === true`). Pure + side-effect-free so the
 * decision is unit-testable in isolation.
 */
export function shouldPrefetch(connection?: SaveDataConnection | null): boolean {
	return connection?.saveData !== true;
}

/** The active `navigator.connection`, or `null` where the API (or `navigator` itself) is absent. */
export function activeConnection(): SaveDataConnection | null {
	if (typeof navigator === "undefined") {
		return null;
	}
	return (navigator as Navigator & {connection?: SaveDataConnection }).connection ?? null;
}

/**
 * Warm the chunk for a single private route (pointer/focus intent). Idempotent: `import()` dedupes,
 * so repeated calls after the first are cheap no-ops. Returns the loader promise, or `undefined` when
 * the path is not a private route or prefetching is disabled by the data-saver preference.
 */
export function preloadRoute(path: string): Promise<unknown> | undefined {
	if (!shouldPrefetch(activeConnection())) {
		return undefined;
	}
	return PRIVATE_ROUTE_LOADERS[path]?.();
}

/**
 * Warm ALL four private-route chunks (the post-login idle warm-up). Guarded once by the data-saver
 * preference; each individual loader is idempotent, so this is safe to call more than once.
 */
export function preloadAllPrivateRoutes(): void {
	if (!shouldPrefetch(activeConnection())) {
		return;
	}
	for (const loader of Object.values(PRIVATE_ROUTE_LOADERS)) {
		loader();
	}
}
